import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { resolvePartnerBranding } from "@/lib/partnerConfig";
import { setBrandConfig } from "@/lib/brandConfig";
import { applyBrandTheme, WIDGET_ROOT_ATTR } from "@/lib/applyTheme";
import { openWidgetModal } from "@/lib/widgetModal";

// Captured synchronously at top-level script execution — document.currentScript
// is only valid during that window (works fine with `defer`, but becomes null
// after any await/microtask), so this must happen before any async code runs.
const THIS_SCRIPT_SRC = document.currentScript?.src || null;

function deriveWidgetCssUrl() {
  const scriptSrc = THIS_SCRIPT_SRC
    || document.querySelector('script[src*="/static/js/next-plugin.js"]')?.src;
  if (!scriptSrc) return null;
  try {
    const url = new URL(scriptSrc, window.location.href);
    url.pathname = url.pathname.replace("/static/js/", "/static/css/").replace(/\.js$/, ".css");
    return url.toString();
  } catch {
    return null;
  }
}

// Mounts the widget isolated inside a shadow root, so host page CSS (eg. a
// generic `div { border: ... }` rule) can never leak in, and the widget's
// own styles can never leak out. Tailwind's reset/preflight layer is
// unscoped (`*,:after,:before{...}`) and has near-zero specificity, so
// without this a host page's plain element selectors reliably win.
const mountShadowed = (hostElement) => {
  const shadowRoot = hostElement.shadowRoot || hostElement.attachShadow({ mode: "open" });
  shadowRoot.innerHTML = "";

  const cssUrl = deriveWidgetCssUrl();
  if (cssUrl) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = cssUrl;
    shadowRoot.appendChild(link);
  } else {
    console.warn("Nucar Next: could not determine widget CSS URL — styles may not load.");
  }

  // Existing CSS keeps its #nucar-next-widget selectors unchanged by giving
  // it a real element with that id to match, now living inside the shadow
  // root instead of the host page's light DOM.
  const innerRoot = document.createElement("div");
  innerRoot.id = "nucar-next-widget";
  shadowRoot.appendChild(innerRoot);

  return innerRoot;
};

// bar: renders the ~100px condensed strip (VINEntryScreen's bar branch).
// initialVehicle + onVehicleDecoded are a matched pair used by the bar ->
// modal handoff pattern: a bar mount decodes a VIN/plate itself and calls
// onVehicleDecoded instead of navigating internally, so a *separate* widget
// instance can be mounted pre-seeded with that vehicle via initialVehicle,
// landing straight on the vehicle-confirm screen. bootWidget below supplies
// the default handler (lib/widgetModal.js's dialog); a host page only needs
// to pass its own onVehicleDecoded to take the handoff over.
// Returns the React root so a caller that owns the mount's lifetime (again,
// widgetModal.js) can unmount it.
const mountApp = (element, { compact = false, bar = false, initialVehicle = null, onVehicleDecoded = null } = {}) => {
  if (!element) return null;
  // Lets applyTheme.js's partner CSS custom properties target every widget
  // host, including ones with a mount id of their own and the modal overlay
  // — not just the literal #nucar-next-widget / #root ids.
  element.setAttribute(WIDGET_ROOT_ATTR, "");
  // Our own standalone site's public/index.html marks its mount div
  // data-standalone="true" (id stays "nucar-next-widget" — Tailwind's
  // `important: '#nucar-next-widget'` selector requires that id as an
  // ancestor to match at all, on every deployment mode). Real third-party
  // host pages embedding the widget never set this attribute, so they still
  // get shadow-DOM isolation + MemoryRouter. The "root" id is a secondary
  // fallback for manual local testing without any #nucar-next-widget markup
  // — mounted plainly, without shadow isolation, so React/CSS devtools work
  // normally.
  const isEmbeddedWidget = element.id !== "root" && element.dataset.standalone !== "true";
  const target = isEmbeddedWidget ? mountShadowed(element) : element;
  const root = ReactDOM.createRoot(target);
  root.render(
    <React.StrictMode>
      <App
        isEmbeddedWidget={isEmbeddedWidget}
        compact={compact}
        bar={bar}
        initialVehicle={initialVehicle}
        onVehicleDecoded={onVehicleDecoded}
        // Radix components that portal to document.body by default (Select,
        // Tooltip, Dialog, ...) need to land back inside #nucar-next-widget's
        // subtree, or Tailwind's `important: '#nucar-next-widget'` scoping
        // never matches them (document.body is an ancestor of
        // #nucar-next-widget, not a descendant) and they render fully
        // unstyled — same failure whether or not there's a shadow root
        // involved, so this is passed for every mount mode, not just the
        // shadow-DOM one. Passed as a prop (scoped to this <App> tree via
        // context) rather than a shared singleton, since a page can have
        // more than one widget instance mounted at once (eg. the bar +
        // modal handoff pattern) and each needs its own portal target.
        shadowContainer={target}
      />
    </React.StrictMode>,
  );
  return root;
};

const resolvePartnerKey = (element) => {
  if (element?.dataset?.partner) return element.dataset.partner;
  // Dev-only convenience for local testing; never active in the built widget.
  if (process.env.NODE_ENV !== "production") {
    const fromQuery = new URLSearchParams(window.location.search).get("partner");
    if (fromQuery) return fromQuery;
  }
  return undefined;
};

const resolveCompact = (element, explicitCompact) => {
  if (typeof explicitCompact === "boolean") return explicitCompact;
  return element?.dataset?.compact === "true";
};

const resolveBar = (element, explicitBar) => {
  if (typeof explicitBar === "boolean") return explicitBar;
  return element?.dataset?.bar === "true";
};

// Bar mode continues in a modal dialog by default — the bar itself is a
// ~100px strip with nowhere to render the rest of the flow, so "Explore My
// Options" has to open *something*. Owning that here (lib/widgetModal.js)
// rather than leaving it to the host page means a plain
// `<div data-bar="true">` embed works on its own, and a dealer site that
// wired the bar up without the reference helper's CSS can't end up dumping
// the flow inline at the bottom of the page instead of in a dialog.
// A host page passing its own onVehicleDecoded still takes over completely.
const resolveVehicleDecodedHandler = (bar, partnerKey, explicitHandler) => {
  if (explicitHandler) return explicitHandler;
  if (!bar) return null;
  return (vehicle) => openWidgetModal({ mount: mountApp, partnerKey, initialVehicle: vehicle });
};

// Resolves + applies partner branding (if a key is present) then mounts.
// When no partner key is found, this mounts synchronously with zero added
// network calls or latency, preserving today's exact default behavior.
const bootWidget = (element, options = {}) => {
  const partnerKey = options.partnerKey || resolvePartnerKey(element);
  const compact = resolveCompact(element, options.compact);
  const bar = resolveBar(element, options.bar);
  const mountOptions = {
    compact,
    bar,
    initialVehicle: options.initialVehicle,
    onVehicleDecoded: resolveVehicleDecodedHandler(bar, partnerKey, options.onVehicleDecoded),
  };

  if (!partnerKey) {
    mountApp(element, mountOptions);
    return;
  }

  // Set regardless of whether the branding fetch below succeeds — other
  // backend calls (eg. ADF lead delivery on /offers/lock) key off this
  // independently of whether Sanity branding data was found.
  setBrandConfig({ partnerKey });

  resolvePartnerBranding(partnerKey)
    .then((branding) => {
      if (branding) {
        setBrandConfig(branding);
        applyBrandTheme(branding.theme);
      }
    })
    .catch(() => {
      // resolvePartnerBranding already swallows its own errors; this is a
      // final safety net so mounting is never blocked.
    })
    .finally(() => mountApp(element, mountOptions));
};

// Expose a global init function for manual control. options.compact renders
// a minimal, form-only version of the widget (no marketing copy) — meant
// for small-footprint embeds like the inline-under-a-button pattern, where
// a whole landing page's worth of content isn't appropriate. options.bar
// renders the further-condensed ~100px strip variant, which continues in
// the widget's own modal dialog after a successful decode with no host-page
// wiring needed; pass options.onVehicleDecoded only to take that handoff
// over from the host page instead. options.initialVehicle seeds a mount
// with an already-decoded vehicle so it opens on the vehicle-confirm screen
// instead of blank at VIN entry (that's how the modal above is mounted).
window.initNucarNext = (targetElementId = "nucar-next-widget", options = {}) => {
  const el = document.getElementById(targetElementId);
  if (el) {
    bootWidget(el, options);
  } else {
    console.error(`Nucar Next: Target element #${targetElementId} not found.`);
  }
};

// Auto-initialize if the default target element exists
const widgetRoot = document.getElementById("nucar-next-widget");
if (widgetRoot) {
  bootWidget(widgetRoot);
} else {
  // Fallback to "root" for local development (e.g. yarn start)
  const devRoot = document.getElementById("root");
  if (devRoot) bootWidget(devRoot);
}
